import {
    AfterViewInit,
    OnInit,
    Component,
    ElementRef,
    HostBinding,
    Input,
    TemplateRef,
    ViewChild,
    ChangeDetectionStrategy,
    Inject,
} from '@angular/core';
import { SafeStyle } from '@angular/platform-browser';
import { WINDOW } from '@ng-web-apis/common';
import { Store } from '@ngrx/store';
import { asyncScheduler, from, fromEvent, interval, Observable, Subject } from 'rxjs';
import {

    debounceTime, distinctUntilChanged, mergeMap, skipWhile, take, takeUntil, throttleTime,
} from 'rxjs/operators';
// eslint-disable-next-line import/named
import SwiperCore, { Scrollbar, SwiperOptions, Pagination, Autoplay } from 'swiper';
import { SwiperComponent } from 'swiper/angular';
import { AutoplayOptions, PaginationOptions } from 'swiper/types';

import {
    SlotResponsiveConfig,
    Slot,
    SLICE_SLIDER_DEFAULT_DELAY,
    SLICE_SLIDER_DEFAULT_NUMBER_OF_SLIDES,
    TypeOfArrow,
    TypeOfScroll,
    TypeOfTheme,
    TypePositionOfPager,
} from './slice-slider.models';

import { CorePlatformService } from '@core/data-providers/platform/platform.service';
import { Resolutions } from '@core/domain/state/core.state';
import { EnvironmentCmsUrls } from '@core/domain/types/environment-config.types';
import { getResolution } from '@core/infrastructure/state/core.selectors';
import { ConfigService } from '@core/services/config/config.service';
import { TypeOfPager } from '@features/cms-render/domain/models/common-settings-styles.models';
import { ContentItemTree } from '@features/cms-render/domain/models/contentItemTree';
import {
    CommonStylesRenderDirective,
} from '@features/cms-render/ui/directives/common-styles-render/common-styles-render.directive';
import { AssetsSharedService } from '@features/cms-render/ui/services/assets-shared/assets-shared.service';
import { deepClone, isDefined } from '@frontend/common/utils';

SwiperCore.use([Pagination, Autoplay, Scrollbar]);

@Component({
    selector: 'slice-slider',
    templateUrl: './slice-slider.component.html',
    styleUrls: ['./slice-slider.component.scss', './slice-slider-cms-partial.component.scss', './slice-slider-partial.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SliceSliderComponent extends CommonStylesRenderDirective implements OnInit, AfterViewInit {
    // Variables Dutti

    @Input() public element: ContentItemTree;

    @HostBinding('style.background-position')
    public backgroundPosition!: string | undefined;

    @HostBinding('style.background-repeat')
    public backgroundRepeat!: string | undefined;

    @HostBinding('style.background-size')
    public backgroundSize!: string | undefined;

    @HostBinding('style.background-attachment')
    public backgroundAttachment!: string | undefined;

    @HostBinding('style.background-image')
    public backgroundImage: SafeStyle | undefined;

    public slots?: Slot[] = [];

    public config: SwiperOptions = {};

    public currentIndex: number = 0;

    public slides!: ContentItemTree[];

    public slidesStyle: any;

    public initialOffset: number | string;

    public emptySlides: string[] = [];

    private didReset: boolean = false;

    public arrowTitles: string[] = [];

    private readonly clearSubscriptions$: Subject<void> = new Subject();

    public disablePrevArrow: boolean;

    public disableNextArrow: boolean;

    private isSwiperInitialized = false;

    public piece$: Observable<any>;

    public navigationEvents$: Observable<any> = from(['mousedown', 'keydown'])
        .pipe(
            mergeMap((event) => fromEvent(this.window, event)),
            throttleTime(500, asyncScheduler, { leading: false, trailing: true }),
            takeUntil(this.clearSubscriptions$)
        );

    public windowEvents$: Observable<any> = fromEvent(this.window, 'resize')
        .pipe(debounceTime(100), takeUntil(this.clearSubscriptions$));

    public imagesLoaded$: Observable<any> = interval(50).pipe(
        skipWhile(() => {
            // 'elementRef' is private in the SwiperComponent class but useful here
            const swiperComponentAsElement = (
                (this.swiperComponent as unknown) as TemplateRef<SwiperComponent>
            ).elementRef;
            const images = Array.prototype.slice.call(swiperComponentAsElement.nativeElement.querySelectorAll('.swiper-slide render-slice-slot img'));
            return images.filter(image => !image.complete).length > 0;
        }),
        take(1),
        takeUntil(this.clearSubscriptions$)
    );

    // Variables CMS

    private pagerStyle: TypeOfPager;

    private pagerPosition: TypePositionOfPager;

    private hasThemeColors: boolean;

    public arrowType: TypeOfArrow;

    public arrowIsIcon: boolean;

    public arrowIsCustomIcon: boolean;

    public arrowIsCustomIconPrev: string;

    public arrowIsCustomIconNext: string;

    public arrowIsTitle: boolean;

    public arrowIsTitleTextPrev: string;

    public arrowIsTitleTextNext: string;

    private scrollMode: TypeOfScroll;

    public isScrollPager: boolean;

    public styleTheme: TypeOfTheme;

    public autoplayDelay: number[] | number;

    private numberOfSlides: number;

    public showSliderHeader: boolean;

    @ViewChild('swiper') public swiperComponent?: SwiperComponent;

    @ViewChild('sliceSliderWrapper') public sliceSliderWrapper!: ElementRef<HTMLElement>;

    // HostBinding CMS

    @HostBinding('style')
    public stylesProperties: string | undefined;

    @HostBinding('class.pager-style-dot')
    public isPagerStyleDot!: boolean;

    @HostBinding('class.pager-style-box')
    public isPagerStyleBox!: boolean;

    @HostBinding('class.pager-style-solid')
    public isPagerStyleSolid!: boolean;

    @HostBinding('class.pager-style-dashed')
    public isPagerStyleDashed!: boolean;

    @HostBinding('class.pager-style-count')
    public isPagerStylecount!: boolean;

    @HostBinding('class.pager-style-scroll')
    public isPagerStyleScroll!: boolean;

    @HostBinding('class.pager-position-left')
    public isPagerPositionLeft!: boolean;

    @HostBinding('class.pager-position-center')
    public isPagerPositionCenter!: boolean;

    @HostBinding('class.pager-position-right')
    public isPagerPositionRight!: boolean;

    @HostBinding('class.pager-position-outside')
    public isPagerPositionOutside!: boolean;

    @HostBinding('class.pager-position-inside')
    public isPagerPositionInside!: boolean;

    @HostBinding('class.swiper-separator')
    public isSeparator!: boolean;

    @HostBinding('style.--swiper-space-between')
    public swiperSpaceBetween!: number;

    @HostBinding('class.slice-slider-horizontal')
    public swiperDirectionHor!: boolean;

    @HostBinding('class.slice-slider-vertical')
    public swiperDirectionVer!: boolean;

    @HostBinding('class.dark-theme')
    public isDarkTheme!: boolean;

    @HostBinding('class.light-theme')
    public isLightTheme!: boolean;

    @HostBinding('class.swiper-freeMode')
    public isScrollFree!: boolean;

    public isBrowser = this.platform.isBrowser();

    constructor(
        @Inject(WINDOW)protected readonly window: Window,
        protected readonly el: ElementRef,
        private readonly assetsSharedService: AssetsSharedService,
        private readonly configService: ConfigService,
        private readonly store: Store,
        private readonly platform: CorePlatformService,
    ) {
        super();
    }

    public ngOnInit(): void {
        this.store.select(getResolution).pipe(
            distinctUntilChanged()
        ).subscribe((resolution: Resolutions) => {
            // TODO ECOMDUTI-20989 CANCELAR SUBSCRIPCION
            this.onSliceInit(resolution);
        });
    }

    public ngAfterViewInit(): void {
        this.el.nativeElement.dir = this.direction;
        this.disablePrevArrow = !!!this.element?.settings?.general.loop && this.direction === 'ltr';
        this.disableNextArrow = !!!this.element?.settings?.general.loop && this.direction === 'rtl';

        this.resetSwiper();
        /** Handle mouse navigate class on this encapsuled component */
        this.handleMouseNavigate();
    }

    protected onSliceInit(resolution: Resolutions): void {
        this.element = deepClone(this.element);
        this.slots = this.element?.settings.slots;
        this.setDefaultHostStyles();
        this.backgroundImageSetting = this.element?.settings?.styles?.backgroundImage;
        this.backgroundPosition = this.backgroundImageSetting ? this.backgroundImageSetting.position ?? 'top left' : undefined;
        this.initSlidesAndArrowTitles();
        this.setConfig(resolution);
        this.initConfig();
        this.subscribeToWindowEvents();
    }

    public resetSwiperAfterLazyImagesLoad(): void {
        // Resetting Swiper causes init() to be called, which triggers the reset again, and so infinitely. We prevent it.
        if (this.didReset) return;

        this.didReset = true;

        if (this.config.freeMode && this.config.direction === 'horizontal') {
            this.imagesLoaded$.subscribe(() => {
                this.resetSwiper();
            });
        }
    }

    public updateState(): void {
        if (!this.isSwiperInitialized || this.swiperComponent.swiperRef.destroyed) {
            return;
        }

        // Swiper works differently when the loop is enabled
        if (this.config.loop) {
            const activeIndex = this.swiperComponent.swiperRef.activeIndex - 1;
            const totalSlides = this.swiperComponent.swiperRef.slides ? this.swiperComponent.swiperRef.slides.length - 2 : 0;
            // Modulus is calculated because there are extra slides
            this.currentIndex = (activeIndex % totalSlides + totalSlides) % totalSlides;
        } else {
            this.currentIndex = this.swiperComponent.swiperRef.activeIndex;
            this.updateArrows();
        }

        // Set the right delay for the current slide
        const delay = this.autoplayDelay;
        // Comprobamos que el autoplay esté activo para evitar un bug de cms cuando autoplay es false
        if (this.element?.settings.general.autoplay && Array.isArray(delay)) {
            (this.swiperComponent.swiperRef.params.autoplay as AutoplayOptions).delay = delay[this.currentIndex] * 1000;
        }

        // Update slice-slider theme every time current slide changes
        this.styleTheme = this.getStyleTheme();
        this.isDarkTheme = this.styleTheme === 'dark-theme';
        this.isLightTheme = this.styleTheme === 'light-theme';

        if (this.arrowIsTitle) {
            this.arrowIsTitleTextPrev = this.getArrowText('prev');
            this.arrowIsTitleTextNext = this.getArrowText('next');
        }
    }

    public arrowClick(direction: 'next' | 'prev'): void {
        if (direction === 'next') {
            if (this.direction === 'rtl') {
                this.swiperComponent?.swiperRef.slidePrev();
            } else {
                this.swiperComponent?.swiperRef.slideNext();
            }
        } else if (direction === 'prev') {
            if (this.direction === 'rtl') {
                this.swiperComponent?.swiperRef.slideNext();
            } else {
                this.swiperComponent?.swiperRef.slidePrev();
            }
        }
    }

    public getArrowText(direction: 'next' | 'prev'): string {
        let title = '';
        const slots = this.element?.settings.slots ?? [];
        if (this.arrowType === 'title' && slots?.length > 0) {
            let titleIndex: number;
            if (direction === 'prev') {
                titleIndex = this.currentIndex > 0 ? this.currentIndex - 1 : slots.length - 1;
            } else {
                titleIndex = this.currentIndex < slots.length - 1 ? this.currentIndex + 1 : 0;
            }
            title = this.arrowTitles[titleIndex];
        }
        return title;
    }

    public getArrowIcon(direction: 'next' | 'prev'): string | undefined {
        const assetsUrl = `${this.configService.getConfigUrl<EnvironmentCmsUrls>('cmsUrls').content}/assets/`;
        const icon = this.element.settings.general.arrowCustomIcon[`${direction}Arrow`] as string;

        const akamaiImageVideoManagerConfig = {
            enabled: true,
            width: this.assetsSharedService.getWidth(this.el),
        };

        return this.assetsSharedService.getAssetUrl(icon, assetsUrl, akamaiImageVideoManagerConfig);
    }

    public trackByIndex(index: number): number {
        return index;
    }

    // Esta funcion no es exactamente igual a la de CMS debido a la estructura de componentes del render de dutti
    private initSlidesAndArrowTitles(): void {
        if (this.slots.length) this.element.children.forEach((child) => this.hasContent(child));

        let children = [];
        this.element.children.forEach((child) => {
            if (child !== null && !this.emptySlides.includes(child?.id)) {
                children.push(child);
            }
        });
        this.element.children = children;
        this.slides = this.element.children;
        this.slidesStyle = this.setSlidesStyle(this.slots);
        this.arrowTitles = this.element.settings.general.titles as string[];
    }

    private setConfig(resolution: Resolutions): void {
        this.autoplayDelay = this.element?.settings.general?.delay ?? SLICE_SLIDER_DEFAULT_DELAY;
        this.pagerStyle = this.element?.settings?.general?.paginationType ?? 'dot';
        this.pagerPosition = this.element?.settings?.general?.pagerPosition ?? 'inside-center';
        this.arrowType = this.element?.settings?.general.arrowType ?? 'icon';
        this.scrollMode = this.element?.settings?.general?.scrollMode ?? 'free';
        this.initialOffset = this.element?.settings?.general?.initialOffset ?? 0;
        this.isPagerStyleDot = this.pagerStyle === 'dot';
        this.isPagerStyleBox = this.pagerStyle === 'box';
        this.isPagerStyleSolid = this.pagerStyle === 'solid';
        this.isPagerStyleDashed = this.pagerStyle === 'dashed';
        this.isPagerStylecount = this.pagerStyle === 'count';
        this.isPagerStyleScroll = this.pagerStyle === 'scroll';
        this.isSeparator = this.element?.settings?.general?.separator ?? false;
        this.swiperSpaceBetween = this.element?.settings?.general?.spaceBetween ?? 0;
        this.swiperDirectionHor = this.element?.settings.general.direction === 'horizontal';
        this.swiperDirectionVer = this.element?.settings.general.direction === 'vertical';
        this.styleTheme = this.getStyleTheme();
        this.isDarkTheme = this.styleTheme === 'dark-theme';
        this.isLightTheme = this.styleTheme === 'light-theme';
        this.isScrollFree = this.scrollMode === 'free';
        this.arrowIsIcon = !!this.element?.settings?.general.showArrows || this.arrowType === 'icon';
        this.arrowIsCustomIcon = this.arrowType === 'custom-icon';

        if (this.arrowIsCustomIcon) {
            this.arrowIsCustomIconPrev = this.getArrowIcon('prev');
            this.arrowIsCustomIconNext = this.getArrowIcon('next');
        }

        this.arrowIsTitle = !!this.element?.settings?.general.showTitles && !this.arrowIsIcon || this.arrowType === 'title';

        if (this.arrowIsTitle) {
            this.arrowIsTitleTextPrev = this.getArrowText('prev');
            this.arrowIsTitleTextNext = this.getArrowText('next');
        }

        this.isScrollPager = this.scrollMode === 'pager';
        this.numberOfSlides = this.getNumberOfSlides(resolution);
        this.stylesProperties = this.setStylesProperties();
        this.updatePagerPosition();

        this.showSliderHeader = this.element.settings.general.showSliderTitle
            && this.element.settings.general.sliderTitle?.length > 0
            || this.element.settings.general.arrowPosition === 'header-right';

        this.hasThemeColors = !!this.element?.settings?.styles?.primaryLightColor
            && !!this.element?.settings?.styles?.secondaryLightColor
            && !!this.element?.settings?.styles?.primaryDarkColor
            && !!this.element?.settings?.styles?.secondaryDarkColor;
    }

    private initConfig(): void {
        const delay = Array.isArray(this.autoplayDelay) ? this.autoplayDelay[0] : this.autoplayDelay;

        this.config = {
            autoHeight: false,
            direction: this.element?.settings.general.direction,
            freeMode: this.isScrollFree,
            init: false,
            loop: !!this.element?.settings.general.loop,
            navigation: false,
            observer: true,
            resizeObserver: true,
            pagination: false,
            slidesPerView: this.numberOfSlides,
            spaceBetween: this.element?.settings.general.spaceBetween,
            updateOnImagesReady: true,
            autoplay: this.element?.settings.general.autoplay
                ? { delay: delay * 1000, stopOnLastSlide: true }
                : false,
            speed: (this.element?.settings.general.speed ?? 1) * 1000,
            centeredSlides: this.element?.settings.general.centeredSlides,
            scrollbar: false,
        };

        if (this.config.freeMode) {
            this.config.slidesPerView = 'auto';
        } else if (this.element?.settings.general.slidesWidthType === 'manual') {
            this.config.slideClass = 'auto-width';
            this.config.slidesPerView = 'auto';
        }

        // Retrocompatibilidad con Home 2022, se puede quitar este if cuando se cambie el diseño (Con las maquetas antiguas no viene la propiedad pagination)
        if (!this.element?.settings.general.pagination && this.isPagerStyleScroll) {
            this.pagerPosition = 'outside-center';
            this.updatePagerPosition();
            this.arrowIsIcon = true;
            this.showSliderHeader = true;
            this.element.settings.general.arrowPosition = 'header-right';
            this.config.pagination = false;
            this.config.observeParents = true;
        } else if (this.element?.settings.general.pagination && this.isScrollPager) {
            if (this.isPagerStyleScroll) {
                this.config.observeParents = true;
            } else if (!this.isPagerStylecount) {
                this.config.pagination = this.getPaginationOptions();
            }
        }
    }

    private getPaginationOptions(): PaginationOptions {
        let bulletClass = 'swiper-pagination-bullet';
        if (this.isPagerStyleBox) {
            bulletClass = `${bulletClass} swiper-pagination-bullet-box`;
        } else if (this.isPagerStyleSolid) {
            bulletClass = `${bulletClass} swiper-pagination-bullet-solid`;
        } else if (this.isPagerStyleDashed) {
            bulletClass = `${bulletClass} swiper-pagination-bullet-dashed`;
        }
        return {
            clickable: true,
            renderBullet: (index: number): string => {
                return `<button class="${bulletClass}">${this.isPagerStyleBox ? index + 1 : ''}</button>`;
            },
        };
    }

    private resetSwiper(): void {
        this.swiperComponent?.swiperRef?.destroy();
        this.swiperComponent?.initSwiper();
        this.isSwiperInitialized = true;
    }

    private handleMouseNavigate(): void {
        if (!isDefined(this.sliceSliderWrapper)) return;
        this.sliceSliderWrapper.nativeElement?.classList.add('mouse-navigate');

        this.navigationEvents$.subscribe((event) => {
            this.updateNavigation(event);
        });
    }

    private updateNavigation(event: Event): void {
        if (event.type === 'mousedown') {
            this.sliceSliderWrapper.nativeElement?.classList.add('mouse-navigate');
        }
        if (event.type === 'keydown') {
            const eventKeyboard = event as KeyboardEvent;
            if (eventKeyboard.key === 'Tab') {
                this.sliceSliderWrapper.nativeElement?.classList.remove('mouse-navigate');
            }
        }
    }

    // Esta funcion no es exactamente igual a la de CMS debido a la estructura de componentes del render de dutti
    private hasContent(slideComponent: ContentItemTree): boolean {
        if (!slideComponent) return false;

        if (slideComponent?.definitionId?.includes('piece-')) {
            return true;
        }
        const children = slideComponent?.children;

        const hasContent = children?.some((child: ContentItemTree) => {
            return child?.definitionId?.includes('slice-')
                ? this.hasContent(child)
                : child?.definitionId?.includes('piece-');
        });
        if (!hasContent) {
            this.emptySlides.push(slideComponent.id);
        }
        return hasContent ?? false;
    }

    public subscribeToWindowEvents(): void {
        if (this.config.loop) {
            this.windowEvents$
                .subscribe(() => {
                    this.resetSwiper();
                });
        }
    }

    public setSlidesStyle(slots: any): Array<Record<string, string>> {
        const styles: Array<Record<string, string>> = [];
        slots.forEach(slot => {
            const responsiveConfig: SlotResponsiveConfig | undefined = slot.responsiveConfig?.find(
                (item) => item.id === this.respConfig?.id
            );
            const style = {
                'flex-grow': slot.flexGrow ?? '0',
                'flex-shrink': slot.flexShrink ?? '1',
                'flex-basis': slot.flexBasis ?? 'auto',
                width: responsiveConfig?.width ?? slot.width ?? '100%',
                height: slot.height ?? 'auto',
                'min-height': responsiveConfig?.minHeight ?? slot.minHeight,
                'max-height': responsiveConfig?.maxHeight ?? slot.maxHeight,
                'min-width': responsiveConfig?.minWidth ?? slot.minWidth,
                'max-width': responsiveConfig?.maxWidth ?? slot.maxWidth,
            };
            styles.push(style);
        });
        return styles;
    }

    // Disable arrows in case we are in first or last slide (Only for no loop mode)
    public updateArrows(): void {
        if (!!!this.element?.settings.general.loop) {
            if (this.direction === 'rtl') {
                this.disablePrevArrow = this.swiperComponent?.swiperRef?.isEnd;
                this.disableNextArrow = this.swiperComponent?.swiperRef?.isBeginning;
            } else {
                this.disablePrevArrow = this.swiperComponent?.swiperRef?.isBeginning;
                this.disableNextArrow = this.swiperComponent?.swiperRef?.isEnd;
            }
        }
    }

    // Get number of slides for desktop and mobile cms setting
    private getNumberOfSlides(resolution: Resolutions): number {
        const desktopSlides = this.element?.settings.general.numberOfSlides ?? SLICE_SLIDER_DEFAULT_NUMBER_OF_SLIDES;
        if (resolution === Resolutions.MOBILE) {
            return this.element?.settings.general.numberOfSlidesMobile ?? desktopSlides;
        }
        return desktopSlides;
    }

    // Set arrows and theme styles for desktop and mobile cms setting
    private setStylesProperties(): string | undefined {
        let style = '';
        if (this.element?.settings?.general?.arrowSize) {
            style += `--arrow-width-desktop: ${this.element.settings.general.arrowSize.widthDesktop};
        --arrow-height-desktop: ${this.element.settings.general.arrowSize.heightDesktop};
        --arrow-width-mobile: ${this.element.settings.general.arrowSize.widthMobile};
        --arrow-height-mobile: ${this.element.settings.general.arrowSize.heightMobile};`;
        }
        if (this.hasThemeColors) {
            style += `--primary-light-color: ${this.element?.settings?.styles?.primaryLightColor};
        --secondary-light-color: ${this.element?.settings?.styles?.secondaryLightColor};
        --primary-dark-color: ${this.element?.settings?.styles?.primaryDarkColor};
        --secondary-dark-color: ${this.element?.settings?.styles?.secondaryDarkColor};`;
        }
        return style ?? undefined;
    }

    // Get current slide theme
    public getStyleTheme(): TypeOfTheme {
        const currentSlide = this.slides?.[this.currentIndex];
        if (currentSlide) {
            if (currentSlide?.settings?.general?.theme) {
                return currentSlide?.settings?.general?.theme === 'dark' ? 'dark-theme' : 'light-theme';
            }
        }
        return 'light-theme';
    }

    public updatePagerPosition(): void {
        this.isPagerPositionLeft = this.pagerPosition.includes('left');
        this.isPagerPositionCenter = this.pagerPosition.includes('center');
        this.isPagerPositionRight = this.pagerPosition.includes('right');
        this.isPagerPositionOutside = this.pagerPosition.includes('outside');
        this.isPagerPositionInside = this.pagerPosition.includes('inside');
    }
}
